import { getQueryKey } from "@trpc/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { trpc, trpcVanilla } from "@client/trpc";
import { type LeadClipHintScope } from "@client/offline/offlineCache";
import {
  meetingsVideoMediaInput,
  workspaceMeetingsInputForPath,
  type MeetingsInput,
} from "@lib/funderWorkspaceRoutes";
import { beginIdentityScopedPrefetch } from "./prefetchIdentityGuard";
import {
  hintViewerUserId,
  videoMediaQueryOptions,
  warmHintedLeadClipMedia,
} from "./prefetchWorkspaceProposalClips";

// The pure URL parsing lives in the shared lib so the server's early-fetch
// shell injection derives the same getMeetings input from the same URL (see
// funderWorkspaceRoutes.ts). Re-exported here so existing consumers keep one
// import site for the prefetch family.
export {
  workspaceMediaPathTargeted,
  workspaceMeetingsInputForPath,
  type MeetingsInput,
} from "@lib/funderWorkspaceRoutes";

type MeetingsWithVideoIds = {
  meetings: Array<{
    id: string;
    video: { id: string } | null;
  }>;
};

// Mirror OverviewPanel's initial selection: a valid URL-target meeting wins;
// otherwise it shows the first row. Match on the meeting/unit id rather than
// video.id because transcript-only meetings are valid deep-link targets too.
export function initialMeetingVideoId(
  data: MeetingsWithVideoIds,
  targetMeetingId: string | null = null,
): string | null {
  const targetMeeting =
    targetMeetingId == null
      ? null
      : data.meetings.find((meeting) => meeting.id === targetMeetingId);
  return (targetMeeting ?? data.meetings[0])?.video?.id ?? null;
}

// Transcript search links select their meeting after the video-target effect,
// so they take precedence if both parameters are present. Most links carry
// only one of these parameters; returning one meeting id keeps the prefetch
// selection aligned with the mounted OverviewPanel.
export function workspaceMeetingTargetFromSearch(
  search: string,
): string | null {
  const searchParams = new URLSearchParams(search);
  return searchParams.get("transcript") ?? searchParams.get("video");
}

/**
 * The lead-video hint scope for a meetings rail, derived from the query input
 * both sides already have — the startup prefetch (from the URL) and
 * OverviewPanel (from its own useQuery input). Deriving it from one shared
 * function is what keeps the writer and the reader on the same key; a rail
 * that computed its own string would silently stop hitting the hint.
 *
 * Each rail leads with a different meeting, so the Overview rail and every
 * Tracks rail get their own entry. A URL-targeted link (`?video=` /
 * `?transcript=`) leads with its target rather than the rail's first row, so
 * it is scoped separately too — that keeps a targeted visit from overwriting
 * the plain rail's hint with a video the next untargeted load won't open.
 */
export function meetingsLeadHintScopeId(
  input: MeetingsInput,
  targetMeetingId: string | null,
): string {
  const rail = input.track ? `track:${input.track}` : "overview";
  return `meetings:${rail}:${targetMeetingId ?? "lead"}`;
}

/** The hint scope for a meetings rail, or null when the viewer is unknown. */
export function meetingsLeadHintScope(
  input: MeetingsInput,
  targetMeetingId: string | null,
  effectiveViewerUserId: string | null,
): LeadClipHintScope | null {
  if (effectiveViewerUserId == null) {
    return null;
  }

  return {
    roundId: input.roundId,
    scopeId: meetingsLeadHintScopeId(input, targetMeetingId),
    effectiveViewerUserId,
  };
}

// Warm the getMeetings cache as soon as the round starts loading, instead of
// waiting for the bootstrap to land and the workspace to render before
// OverviewPanel's useQuery fires — on a deep link to the Overview or a Track
// tab that ordering costs about a second, and the poster images and media-URL
// queries chained behind the meetings response all shift late with it. The
// round ID and rail are already in the URL at load time, so the fetch can ride
// alongside getRoundBootstrap; OverviewPanel's useQuery then dedupes onto this
// entry via the shared query key (same technique as the getCurrentUser
// fetchQuery in Round.tsx's loadRoundData).
export function prefetchWorkspaceMeetings(
  queryClient: QueryClient,
  roundId: string,
  pathname: string,
  targetMeetingId: string | null = null,
): void {
  const input = workspaceMeetingsInputForPath(roundId, pathname);

  if (input == null) {
    return;
  }

  const initialMediaInput = meetingsVideoMediaInput(input);

  void (async () => {
    const guard = beginIdentityScopedPrefetch(queryClient);

    try {
      // The pathname-derived request resolves the rail's lead video server-side
      // and rides alongside getMeetings. Query-string targets cannot use that
      // identity, so only those retain the remembered direct-video warm.
      const hintedVideoId =
        targetMeetingId == null
          ? null
          : warmHintedLeadClipMedia(
              queryClient,
              meetingsLeadHintScope(input, targetMeetingId, hintViewerUserId()),
            );

      if (hintedVideoId != null) {
        // Speculative, but still this chain's entry to clean up if the viewer
        // switches while it is in flight.
        guard.warm(videoMediaQueryOptions({ videoId: hintedVideoId }).queryKey);
      } else if (targetMeetingId == null) {
        const mediaOptions = videoMediaQueryOptions(initialMediaInput);
        guard.warm(mediaOptions.queryKey);
        void queryClient.prefetchQuery(mediaOptions);
      }

      const meetings = await queryClient.fetchQuery({
        queryKey: guard.warm(getQueryKey(trpc.getMeetings, input, "query")),
        queryFn: () => trpcVanilla.getMeetings.query(input),
      });

      if (guard.discardIfViewerChanged()) {
        return;
      }

      const videoId = initialMeetingVideoId(meetings, targetMeetingId);

      if (videoId == null) {
        return;
      }

      const mediaOptions = videoMediaQueryOptions(
        targetMeetingId == null ? initialMediaInput : { videoId },
      );
      guard.warm(mediaOptions.queryKey);
      await queryClient.prefetchQuery(mediaOptions);
      guard.discardIfViewerChanged();
    } catch {
      // This is an optimization only. The mounted hooks retain their normal
      // loading and error behavior if either early request fails.
    }
  })();
}
